import { z } from "zod";

import { baseEntityFields } from "./base.js";
import { ContactSchema, contactDisplayName, type ContactNameParts } from "./contact.js";
import { AssetSchema } from "./asset.js";
import { LoanSchema } from "./loan.js";
import { WarrantySchema } from "./warranty.js";
import { IncidentSchema } from "./incident.js";
import { MeasurementSchema } from "./measurement.js";
import { FormSchema } from "./form.js";
import { CampaignSchema } from "./campaign.js";
import { AccessGrantSchema } from "./accessGrant.js";
import { PolicySchema } from "./policy.js";
import { RegisterSessionSchema } from "./registerSession.js";
import { RelationshipSchema } from "./relationship.js";
import { GroupSchema } from "./group.js";
import { PlaceSchema } from "./place.js";
import { PriceListSchema } from "./priceList.js";
import { StockSchema } from "./stock.js";
import { StockMovementSchema } from "./stockMovement.js";
import { AvailabilitySchema } from "./availability.js";
import { ShiftSchema } from "./shift.js";
import { AbsenceSchema } from "./absence.js";
import { AttendanceSchema } from "./attendance.js";
import { SubscriptionSchema } from "./subscription.js";
import { AgreementSchema } from "./agreement.js";
import { DonationSchema } from "./donation.js";
import { ExpenseSchema } from "./expense.js";
import { ShipmentSchema } from "./shipment.js";
import { CustodySchema } from "./custody.js";
import { TicketSchema } from "./ticket.js";
import { InspectionSchema } from "./inspection.js";
import { MessageSchema } from "./message.js";
import { ConversationSchema } from "./conversation.js";
import { ReviewSchema } from "./review.js";
import { ConsentSchema } from "./consent.js";
import { VoucherSchema } from "./voucher.js";
import { CreditSchema } from "./credit.js";
import { CompositionSchema } from "./composition.js";
import { LotSchema } from "./lot.js";
import { RouteSchema } from "./route.js";
import { WorkerSchema } from "./worker.js";
import { ProductSchema } from "./product.js";
import { ResourceSchema } from "./resource.js";
import { BookingSchema } from "./booking.js";
import { EventSchema } from "./event.js";
import { RegistrationSchema } from "./registration.js";
import { OrderSchema } from "./order.js";
import { InvoiceSchema } from "./invoice.js";
import { PaymentSchema } from "./payment.js";
import { WorkOrderSchema } from "./workOrder.js";
import { TaskSchema } from "./task.js";
import { TodoSchema } from "./todo.js";
import { TimesheetSchema } from "./timesheet.js";
import { DocumentSchema } from "./document.js";
import { ContentSchema } from "./content.js";
import { ProjectSchema } from "./project.js";
import { ListingSchema } from "./listing.js";
import { OpportunitySchema } from "./opportunity.js";
import { CareRecordSchema } from "./careRecord.js";
import { CredentialSchema } from "./credential.js";
import { TemplateSchema } from "./template.js";
import { RecurrenceSchema } from "./recurrence.js";
import { FAMILLE_PERSONNES, MARQUE_PERSONNE } from "./personne.js";

/**
 * LES FAMILLES DU SOCLE, DESIGNEES PAR LEUR NOM.
 *
 * Le socle ne connait aucune entite metier concrete : il connait des FAMILLES
 * (AGENTS.md section 7), et jusqu'ici chacune ne se lisait que par son export
 * nomme (`ContactSchema`, `TimesheetSchema`). Un ecran qui INTERPRETE une
 * declaration ne connait la famille qu'a l'execution, comme une chaine
 * (`porte.jeu.family`) : il lui faut la table qui va du nom au schema, et ce
 * fichier est cette table, ecrite UNE fois.
 *
 * LA TABLE EST ECRITE A LA MAIN, ENTREE PAR ENTREE, et c'est voulu : deviner le
 * schema en retirant un `s` au nom (ce que fait `scripts/studio-natifs.mjs`,
 * qui GENERE un catalogue et le compare) marcherait jusqu'au jour ou une
 * famille au nom irregulier arriverait, et rendrait alors le schema d'une
 * voisine sans rien dire. Ici, une famille ajoutee au SDK sans son entree fait
 * echouer le test de recoupement (`packages/sdk/tests/familles.test.mjs`).
 */

/** Le GENRE d'un champ natif, ce que l'ecran generique en fait a l'edition. */
export type GenreDeChamp =
  | "string"
  | "number"
  | "boolean"
  | "date"
  | "enum"
  /**
   * Un `personId` : une personne de l'org (annuaire `personnes`). Marque par
   * `personneId()` (`personne.ts`), lu ici par la description du schema.
   */
  | "personne"
  | "array"
  | "record"
  | "unknown";

/** Un champ NATIF d'une famille : sa clef, son genre, et s'il est requis. */
export interface ChampNatif {
  /** La clef de racine du document (`name`, `email`, `startAt`). */
  key: string;
  genre: GenreDeChamp;
  /** Le schema de la famille l'exige (ni optionnel, ni defaut, ni nullable). */
  requis: boolean;
  /** Les valeurs admises, pour un `enum` seulement. */
  valeurs?: readonly string[];
  /** Pose par le socle, jamais par une saisie : les champs de trace seulement. */
  lectureSeule?: true;
}

/** Le verdict d'une validation de champ natif : la valeur normalisee, ou la raison du refus, en francais. */
export type VerdictNatif =
  | { ok: true; valeur: unknown }
  | { ok: false; raison: string };

/**
 * LA TABLE NOM -> SCHEMA, dans l'ordre de l'objet `sdk` de `createTakiSdk.ts`.
 *
 * `members` et `branding` n'y sont pas, et pour la meme raison qu'ils sont
 * refuses par `sdk.repositoryOf` : l'annuaire des membres vit dans une
 * sous-collection (`orgs/{orgId}/members`), le branding est UN document de
 * reglage. Ni l'un ni l'autre n'a la surface d'une famille.
 */
const SCHEMAS_PAR_FAMILLE = {
  contacts: ContactSchema,
  assets: AssetSchema,
  loans: LoanSchema,
  warranties: WarrantySchema,
  incidents: IncidentSchema,
  measurements: MeasurementSchema,
  forms: FormSchema,
  campaigns: CampaignSchema,
  accessGrants: AccessGrantSchema,
  policies: PolicySchema,
  registerSessions: RegisterSessionSchema,
  relationships: RelationshipSchema,
  groups: GroupSchema,
  places: PlaceSchema,
  priceLists: PriceListSchema,
  stocks: StockSchema,
  stockMovements: StockMovementSchema,
  availabilities: AvailabilitySchema,
  shifts: ShiftSchema,
  absences: AbsenceSchema,
  attendances: AttendanceSchema,
  subscriptions: SubscriptionSchema,
  agreements: AgreementSchema,
  donations: DonationSchema,
  expenses: ExpenseSchema,
  shipments: ShipmentSchema,
  custodies: CustodySchema,
  tickets: TicketSchema,
  inspections: InspectionSchema,
  messages: MessageSchema,
  conversations: ConversationSchema,
  reviews: ReviewSchema,
  consents: ConsentSchema,
  vouchers: VoucherSchema,
  credits: CreditSchema,
  compositions: CompositionSchema,
  lots: LotSchema,
  routes: RouteSchema,
  workers: WorkerSchema,
  products: ProductSchema,
  resources: ResourceSchema,
  bookings: BookingSchema,
  events: EventSchema,
  registrations: RegistrationSchema,
  orders: OrderSchema,
  invoices: InvoiceSchema,
  payments: PaymentSchema,
  workOrders: WorkOrderSchema,
  tasks: TaskSchema,
  todos: TodoSchema,
  timesheets: TimesheetSchema,
  documents: DocumentSchema,
  contents: ContentSchema,
  projects: ProjectSchema,
  listings: ListingSchema,
  opportunities: OpportunitySchema,
  careRecords: CareRecordSchema,
  credentials: CredentialSchema,
  templates: TemplateSchema,
  recurrences: RecurrenceSchema,
} as const satisfies Record<string, z.AnyZodObject>;

/**
 * Les noms des familles exposees par le SDK, au PLURIEL, dans l'ordre de
 * `createTakiSdk`. DERIVES de la table ci-dessus : une seconde liste ecrite a
 * cote serait une liste de plus a tenir a jour, donc une qui serait fausse.
 */
export const FAMILLES: readonly string[] = Object.keys(SCHEMAS_PAR_FAMILLE);

/** Le nom est-il celui d'une famille du socle ? */
export function estFamille(nom: string): boolean {
  return FAMILLES.includes(nom);
}

/**
 * LE VOCABULAIRE D'UNE FAMILLE INCONNUE, ecrit une seule fois pour le
 * data-model ET pour le SDK (`repositoryOf`, `creerDansFamille`, `famille`).
 * Le nom attendu est toujours le meme mot : la clef du repository dans
 * `TakiSdk`, au pluriel, celle de `taki.fields.json` et du `jeu.family` d'une
 * porte.
 */
export function messageFamilleInconnue(famille: string): string {
  return (
    `Famille inconnue : "${famille}". Le nom d'une famille est la clef du repository dans ` +
    "`TakiSdk`, au PLURIEL (`assets`, `bookings`, `careRecords`), le meme mot que la clef " +
    "`family` de taki.fields.json et que le `jeu.family` d'une porte. Le singulier " +
    "(`booking`) est le vocabulaire du catalogue du Studio, pas celui du SDK."
  );
}

/** Le schema Zod complet d'une famille (`XSchema`). LEVE sur un nom inconnu. */
export function schemaDeFamille(famille: string): z.ZodObject<z.ZodRawShape> {
  const schema = (SCHEMAS_PAR_FAMILLE as Record<string, z.AnyZodObject | undefined>)[famille];
  if (schema === undefined) throw new Error(messageFamilleInconnue(famille));
  return schema as z.ZodObject<z.ZodRawShape>;
}

/**
 * LES CLEFS QU'UNE APP NE VOIT PAS : les champs standard poses par le SDK
 * (lus dans `baseEntityFields`, jamais recopies ici), le cadrage `orgId` et le
 * sous-type `type`, qui sont immuables cote Security Rules. `customFields` fait
 * partie des champs standard, et le sac a son propre chemin.
 */
const CLEFS_CACHEES: ReadonlySet<string> = new Set([
  ...Object.keys(baseEntityFields),
  "orgId",
  "type",
]);

/** Les champs natifs deja calcules : la liste d'une famille ne change jamais. */
const memoireDesChamps = new Map<string, readonly ChampNatif[]>();

/** Une enveloppe Zod deballee : le type reel, et si la valeur peut manquer. */
interface Noyau {
  schema: z.ZodTypeAny;
  facultatif: boolean;
}

/**
 * DEBALLE LES ENVELOPPES d'un champ pour atteindre son type reel. Elles
 * s'empilent (`z.string().min(1).optional()`, `z.enum(...).default(...)`) et
 * chacune ment sur le `typeName` de celle du dessous. `optional`, `default` et
 * `nullable` disent en plus que la valeur peut manquer : c'est ce qui fait
 * qu'un champ n'est PAS requis.
 */
function deballer(schema: z.ZodTypeAny): Noyau {
  const kinds = z.ZodFirstPartyTypeKind;
  let courant = schema;
  let facultatif = false;
  for (;;) {
    const def = courant._def as {
      typeName?: z.ZodFirstPartyTypeKind;
      innerType?: z.ZodTypeAny;
      schema?: z.ZodTypeAny;
    };
    const enveloppe =
      def.typeName === kinds.ZodOptional ||
      def.typeName === kinds.ZodDefault ||
      def.typeName === kinds.ZodNullable;
    if (enveloppe && def.innerType !== undefined) {
      facultatif = true;
      courant = def.innerType;
      continue;
    }
    // `ZodEffects` (un `.refine` ou un `.transform`) garde son schema sous une
    // autre clef que les trois precedentes, et ne dit rien du caractere requis.
    if (def.typeName === kinds.ZodEffects && def.schema !== undefined) {
      courant = def.schema;
      continue;
    }
    return { schema: courant, facultatif };
  }
}

/** Le genre d'un type Zod deballe, et les valeurs admises quand c'en est un enum. */
function genreDe(schema: z.ZodTypeAny): { genre: GenreDeChamp; valeurs?: readonly string[] } {
  const kinds = z.ZodFirstPartyTypeKind;
  const def = schema._def as { typeName?: z.ZodFirstPartyTypeKind; values?: unknown };
  // La marque d'une personne est une DESCRIPTION posee sur le `ZodString`
  // (`personneId()`) : elle se lit avant le type, qui dirait « string ».
  if (schema.description === MARQUE_PERSONNE) return { genre: "personne" };
  switch (def.typeName) {
    case kinds.ZodString:
      return { genre: "string" };
    case kinds.ZodNumber:
      return { genre: "number" };
    case kinds.ZodBoolean:
      return { genre: "boolean" };
    case kinds.ZodDate:
      return { genre: "date" };
    case kinds.ZodEnum:
    case kinds.ZodNativeEnum: {
      // `ZodEnum` range ses valeurs dans un tableau, `ZodNativeEnum` dans
      // l'objet de l'enum TypeScript : les deux se lisent par `Object.values`.
      const brutes = Object.values((def.values ?? {}) as Record<string, unknown>);
      return { genre: "enum", valeurs: brutes.filter((v): v is string => typeof v === "string") };
    }
    case kinds.ZodArray:
      return { genre: "array" };
    case kinds.ZodObject:
    case kinds.ZodRecord:
      return { genre: "record" };
    default:
      // Une union, un tuple, un `z.unknown()` : l'ecran generique le montrera
      // en lecture seule plutot que d'inventer une case d'edition.
      return { genre: "unknown" };
  }
}

/**
 * LES CHAMPS NATIFS D'UNE FAMILLE, ceux qu'une app peut lire et ecrire a la
 * racine du document : les champs metier, SANS les champs standard poses par
 * le SDK, sans `orgId` ni `type` (le cadrage et le sous-type ne s'editent pas)
 * et sans `customFields` (le sac a son propre chemin). LEVE sur un nom inconnu.
 */
export function champsNatifs(famille: string): readonly ChampNatif[] {
  const connus = memoireDesChamps.get(famille);
  if (connus !== undefined) return connus;
  const shape = schemaDeFamille(famille).shape;
  const champs: ChampNatif[] = [];
  for (const [key, schema] of Object.entries(shape)) {
    if (CLEFS_CACHEES.has(key)) continue;
    const noyau = deballer(schema);
    const { genre, valeurs } = genreDe(noyau.schema);
    champs.push({
      key,
      genre,
      requis: !noyau.facultatif,
      ...(valeurs === undefined ? {} : { valeurs }),
    });
  }
  memoireDesChamps.set(famille, champs);
  return champs;
}

/* LES CHAMPS DE TRACE vivent avec les champs standard (`base.ts`) : les
   conditions les lisent, et ce module-ci importe tous les schemas. */
export { CHAMPS_DE_TRACE, estChampDeTrace } from "./base.js";
import { CHAMPS_DE_TRACE, estChampDeTrace } from "./base.js";

/** Ce qu'un genre attend, dit en francais : la fin de la phrase « Le champ ... attend ... ». */
const ATTENDU: Readonly<Record<GenreDeChamp, string>> = {
  string: "du texte",
  number: "un nombre",
  boolean: "oui ou non",
  date: "une date",
  enum: "une valeur de la liste",
  personne: "une personne de l'organisation",
  array: "une liste",
  record: "un objet",
  unknown: "une autre forme de valeur",
};

/**
 * NORMALISE CE QUI VIENT DE L'ECRAN avant de le soumettre au schema. Une case
 * rend TOUJOURS du texte : sans cette etape, un nombre saisi correctement
 * serait refuse parce qu'il arrive en chaine, et un espace de trop ferait
 * echouer un `min(1)` que personne n'avait viole.
 */
function normaliser(champ: ChampNatif, valeur: unknown): VerdictNatif {
  const texte = typeof valeur === "string" ? valeur.trim() : null;
  switch (champ.genre) {
    case "string":
    case "personne":
      return { ok: true, valeur: texte ?? valeur };
    case "number": {
      if (texte === null) return { ok: true, valeur };
      const nombre = Number(texte);
      if (Number.isNaN(nombre)) {
        return { ok: false, raison: `Le champ « ${champ.key} » attend un nombre.` };
      }
      return { ok: true, valeur: nombre };
    }
    case "boolean": {
      if (texte === "true") return { ok: true, valeur: true };
      if (texte === "false") return { ok: true, valeur: false };
      return { ok: true, valeur };
    }
    case "date": {
      const date = valeur instanceof Date ? valeur : texte === null ? null : new Date(texte);
      if (date === null) return { ok: true, valeur };
      if (Number.isNaN(date.getTime())) {
        return { ok: false, raison: `Le champ « ${champ.key} » attend une date.` };
      }
      // Une date PURE (`AAAA-MM-JJ`) passe par `new Date` comme une date-heure
      // ISO : c'est le schema de la famille qui a decide laquelle des deux il
      // veut (`z.date()` ici, `z.string()` pour un jour sans heure).
      return { ok: true, valeur: date };
    }
    case "array":
      // UNE LISTE SAISIE EN TEXTE, un element par ligne (ou separes par des
      // virgules) : c'est ainsi qu'une case `textarea` de la fiche ecrit les
      // sauts d'un plan. Le schema juge ensuite chaque element.
      if (texte === null) return { ok: true, valeur };
      return {
        ok: true,
        valeur: texte
          .split(/[\n,]/)
          .map((element) => element.trim())
          .filter((element) => element !== ""),
      };
    default:
      // Enum, objet, inconnu : la valeur brute, c'est le schema qui tranche.
      // Traduire ici ferait deux juges pour une meme question.
      return { ok: true, valeur };
  }
}

/** La raison d'un refus de Zod, dite en francais, en citant la clef. */
function raisonDuRefus(champ: ChampNatif, probleme: z.ZodIssue | undefined): string {
  const generique = `Le champ « ${champ.key} » n'accepte pas cette valeur.`;
  if (probleme === undefined) return generique;
  switch (probleme.code) {
    case "invalid_string":
      return probleme.validation === "email"
        ? "L'email n'a pas la forme attendue (nom@domaine)."
        : `Le champ « ${champ.key} » n'a pas la forme attendue.`;
    case "invalid_type":
      return probleme.expected === "integer"
        ? `Le champ « ${champ.key} » attend un nombre entier.`
        : `Le champ « ${champ.key} » attend ${ATTENDU[champ.genre]}.`;
    case "invalid_enum_value":
      return `Le champ « ${champ.key} » n'accepte que : ${(champ.valeurs ?? []).join(", ")}.`;
    case "too_small":
      return probleme.type === "string" && probleme.minimum === 1
        ? `Le champ « ${champ.key} » ne peut pas etre vide.`
        : `Le champ « ${champ.key} » ne peut pas etre plus petit que ${String(probleme.minimum)}.`;
    case "too_big":
      return `Le champ « ${champ.key} » ne peut pas depasser ${String(probleme.maximum)}.`;
    case "not_finite":
      return `Le champ « ${champ.key} » attend un nombre.`;
    default:
      return generique;
  }
}

/**
 * VALIDE UNE VALEUR POUR UN CHAMP NATIF, avant d'ecrire. Une clef inconnue ou
 * une valeur que le schema refuse rendent `ok: false` avec une raison en
 * francais ; une case videe (`null`, `''`) rend `null` sur un champ facultatif
 * (le SDK lit `null` comme « retire la clef ») et un refus sur un champ requis.
 *
 * LEVE, en revanche, sur une FAMILLE inconnue : ce n'est pas une saisie a
 * corriger, c'est une declaration d'app fausse, et l'afficher dans une pastille
 * de refus la ferait passer pour une faute de l'utilisateur.
 */
export function validerChampNatif(famille: string, key: string, valeur: unknown): VerdictNatif {
  // Un champ de TRACE n'est pas un champ natif, mais le dire ainsi laisserait
  // croire a une faute de frappe : c'est le socle qui le pose.
  if (estChampDeTrace(key)) {
    return { ok: false, raison: `Le champ « ${key} » est pose par le socle : il ne se modifie pas.` };
  }
  const champ = champsNatifs(famille).find((candidat) => candidat.key === key);
  if (champ === undefined) {
    return {
      ok: false,
      raison: `Le champ « ${key} » n'est pas un champ de la famille « ${famille} ».`,
    };
  }

  const vide =
    valeur === null ||
    valeur === undefined ||
    (typeof valeur === "string" && valeur.trim() === "");
  if (vide) {
    // La tournure est celle d'`exigeTexte` (apps/hello/src/apps/contact/views.ts) :
    // un champ que le schema exige ne se vide pas, il se corrige.
    if (champ.requis) {
      return { ok: false, raison: `Le champ « ${key} » ne peut pas etre efface, seulement corrige.` };
    }
    // UNE LISTE VIDEE EST UNE LISTE VIDE, pas `null` : son schema porte un
    // defaut `[]` (les sauts d'un plan), et `null` y serait refuse.
    if (champ.genre === "array") return { ok: true, valeur: [] };
    return { ok: true, valeur: null };
  }

  const normalise = normaliser(champ, valeur);
  if (!normalise.ok) return normalise;

  // La clef vient d'etre trouvee dans `champsNatifs`, donc dans ce meme shape :
  // le repli ne sert qu'a satisfaire l'index verifie (`noUncheckedIndexedAccess`).
  const schemaDuChamp = schemaDeFamille(famille).shape[key];
  if (schemaDuChamp === undefined) {
    return {
      ok: false,
      raison: `Le champ « ${key} » n'est pas un champ de la famille « ${famille} ».`,
    };
  }
  const verdict = schemaDuChamp.safeParse(normalise.valeur);
  if (verdict.success) return { ok: true, valeur: verdict.data };
  return { ok: false, raison: raisonDuRefus(champ, verdict.error.issues[0]) };
}

/* -------------------------------------------------------------------------- */
/* Le titre d'une fiche, par famille                                          */
/* -------------------------------------------------------------------------- */

/**
 * LES CHAMPS NATIFS QUI SAVENT NOMMER UNE FICHE, dans l'ordre ou on les
 * prefere. Un mot par nature de fiche : le nom d'une chose (`name`), le titre
 * d'un ecrit (`title`, `subject`), le numero d'une piece (`number`,
 * `reference`, `code`). C'est une CONVENTION, pas une table par famille : une
 * famille qui gagne un champ `name` est nommee sans qu'une ligne ici change, et
 * la liste ne cite que des mots, jamais une famille.
 */
const CHAMPS_DE_TITRE: readonly string[] = [
  "name",
  "displayName",
  "title",
  "label",
  "subject",
  "number",
  "reference",
  "code",
];

/**
 * LE CHAMP NATIF QUI NOMME LES FICHES D'UNE FAMILLE, ou `undefined` quand
 * aucun ne le sait (un `project` n'a pas de nom natif : c'est un champ
 * sur-mesure de l'app qui le gere qui le nomme, et la reference le declare par
 * `titre`). Derive du schema, memoise, jamais ecrit a la main.
 */
export function champDeTitre(famille: string): string | undefined {
  const connu = memoireDesTitres.get(famille);
  if (connu !== undefined) return connu ?? undefined;
  const shape = schemaDeFamille(famille).shape;
  const champ = CHAMPS_DE_TITRE.find((clef) => clef in shape);
  memoireDesTitres.set(famille, champ ?? null);
  return champ;
}
const memoireDesTitres = new Map<string, string | null>();

/**
 * LE TITRE D'UNE FICHE D'UNE FAMILLE, tel qu'une reference l'affiche quand elle
 * ne declare pas de champ titre : c'est la convention du socle, ecrite UNE
 * fois, pour tous les ecrans et toutes les apps qui designent cette famille.
 *
 * `contacts` a la sienne (`contactDisplayName`, le meme mot que l'app Contact
 * affiche : une personne n'a pas de `name`, elle a un prenom et un nom), les
 * autres suivent `champDeTitre`. RENVOIE `undefined`, JAMAIS UN MOT INVENTE,
 * quand la famille n'a rien pour se nommer ou que le champ est vide : c'est a
 * l'appelant de choisir son repli (l'id, en general), et un « Sans nom » pose
 * ici s'afficherait dans des tableaux qui ont deja leur propre mot pour ca.
 */
export function titreDeFiche(
  famille: string,
  fiche: Readonly<Record<string, unknown>>,
): string | undefined {
  // L'annuaire des personnes n'est pas une famille (`champDeTitre` leverait) :
  // une personne se nomme par son nom, a defaut par son e-mail.
  if (famille === FAMILLE_PERSONNES) {
    for (const clef of ["displayName", "email"]) {
      const valeur = fiche[clef];
      if (typeof valeur === "string" && valeur.trim() !== "") return valeur.trim();
    }
    return undefined;
  }
  if (famille === "contacts") {
    const texte = contactDisplayName(fiche as ContactNameParts);
    return texte === "Contact sans nom" ? undefined : texte;
  }
  const champ = champDeTitre(famille);
  if (champ === undefined) return undefined;
  const valeur = fiche[champ];
  if (typeof valeur === "string") {
    const texte = valeur.trim();
    return texte === "" ? undefined : texte;
  }
  if (typeof valeur === "number") return String(valeur);
  return undefined;
}
